import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import React, { useState, useEffect } from "react";
import Menubar from "../Menubar/Menubar";
import { Outlet } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const KanbanBoard = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  // const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");

    if (savedState !== null) {
      return JSON.parse(savedState);
    }

    return window.innerWidth >= 1024;
  });

  const [tasks, setTasks] = useState({
    PENDING: [],
    PROCESSING: [],
    CONFIRMED: [],
    CANCELLED: [],
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) setIsSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Fetch tasks from backend
  useEffect(() => {
    // inside fetchTasks useEffect
    const fetchTasks = async () => {
      try {
        const res = await axios.get(
          "https://demo.zorbastays.com/web-backend/kanban",
        );
        const data = res.data;

        const grouped = {
          PENDING: [],
          PROCESSING: [],
          CONFIRMED: [],
          CANCELLED: [],
        };

        data.forEach((task) => {
          let status = (task.bookingstatus || "pending").toLowerCase();

          // normalize
          if (status === "booked") status = "confirmed";

          const col = status.toUpperCase();
          if (grouped[col]) {
            grouped[col].push({
              id: task.id,
              title: task.bookingid, // ✅ show bookingid
              desc: `${task.firstname} ${task.lastname || ""} - ${task.email}`, // example desc
            });
          }
        });

        setTasks(grouped);
      } catch (err) {
        console.error("Error fetching bookings:", err);
      } finally {
        setLoading(false); // ✅ stop loader
      }
    };

    fetchTasks();
  }, []);

  const statusColors = {
    CANCELLED: "bg-amber-800/20 text-amber-800",
    CONFIRMED: "bg-green-500/20 text-green-500",
    PROCESSING: "bg-purple-500/20 text-purple-500",
    FAILED: "bg-red-500/20 text-red-500",
    PENDING: "bg-yellow-200/40 text-yellow-600",
  };

  const statuses = Object.keys(tasks);

  // const onDragEnd = async (result) => {
  //   const { source, destination } = result;
  //   if (!destination) return;

  //   if (
  //     source.droppableId === destination.droppableId &&
  //     source.index === destination.index
  //   )
  //     return;

  //   const sourceCol = source.droppableId;
  //   const destCol = destination.droppableId;

  //   const newTasks = { ...tasks };
  //   const [movedTask] = newTasks[sourceCol].splice(source.index, 1);

  //   if (destCol === "CANCELLED") {
  //     const confirmCancel = await Swal.fire({
  //       title: "Cancel Booking?",
  //       text: "Would you like to cancel this booking?",
  //       icon: "warning",
  //       showCancelButton: true,
  //       confirmButtonColor: "#d33",
  //       cancelButtonColor: "#3085d6",
  //       confirmButtonText: "Yes, cancel it!",
  //       cancelButtonText: "No, keep it",
  //     });

  //     if (!confirmCancel.isConfirmed) {
  //       newTasks[sourceCol].splice(source.index, 0, movedTask);
  //       setTasks(newTasks);
  //       return;
  //     }
  //   }

  //   newTasks[destCol].splice(destination.index, 0, movedTask);
  //   setTasks(newTasks);

  //   axios.put(
  //     `https://demo.zorbastays.com/web-backend/kanban/${movedTask.id}`,
  //     {
  //       status: destCol,
  //     }
  //   );

  //   if (destCol === "CANCELLED") {
  //     Swal.fire("Cancelled!", "The booking has been cancelled.", "success");
  //   }
  // };
  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const sourceCol = source.droppableId;
    const destCol = destination.droppableId;

    const newTasks = { ...tasks };
    const [movedTask] = newTasks[sourceCol].splice(source.index, 1);

    if (destCol === "CANCELLED") {
      // Step 1: confirm
      const confirmCancel = await Swal.fire({
        title: "Cancel Booking?",
        text: "Would you like to cancel this booking?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, cancel it!",
        cancelButtonText: "No, keep it",
      });

      if (!confirmCancel.isConfirmed) {
        newTasks[sourceCol].splice(source.index, 0, movedTask);
        setTasks(newTasks);
        return;
      }

      // Step 2: ask reason
      const { value: reason } = await Swal.fire({
        title: "Reason for Cancellation",
        input: "textarea",
        inputPlaceholder: "Enter reason...",
        inputAttributes: { "aria-label": "Reason" },
        showCancelButton: true,
      });

      if (!reason) {
        // if cancelled or empty, restore task
        newTasks[sourceCol].splice(source.index, 0, movedTask);
        setTasks(newTasks);
        return;
      }

      // Step 3: update backend with reason
      newTasks[destCol].splice(destination.index, 0, movedTask);
      setTasks(newTasks);

      try {
        await axios.put(
          `https://demo.zorbastays.com/web-backend/kanban/${movedTask.id}`,
          {
            status: destCol,
            reasonofcancel: reason, // ✅ send reason
          },
        );
        Swal.fire("Cancelled!", "The booking has been cancelled.", "success");
      } catch (err) {
        console.error("Cancel update error:", err);
        Swal.fire("Error", "Could not cancel booking", "error");
      }

      return; // ✅ stop here after cancel flow
    }

    // Normal drag for other statuses
    newTasks[destCol].splice(destination.index, 0, movedTask);
    setTasks(newTasks);

    axios.put(
      `https://demo.zorbastays.com/web-backend/kanban/${movedTask.id}`,
      { status: destCol },
    );
  };

  return (
    <div className="flex bg-[#F8F9FA]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />
      <main
        className={`p-5 lg:p-6 transition-all duration-500 w-full ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <Outlet />
        <div className="p-6 bg-[#F8F9FA] min-h-screen">
          <h2 className="text-xl font-semibold text-gray-700 mb-6">
            Kanban Board
          </h2>
          {loading ? (
            // ✅ Loader UI
            <div className="flex justify-center items-center h-64">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {statuses.map((status) => (
                  <Droppable droppableId={status} key={status}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="bg-white shadow-md rounded-xl p-4 flex flex-col"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h3
                            className={`text-sm font-semibold px-3 py-1 rounded-lg ${statusColors[status]}`}
                          >
                            {status}
                          </h3>
                          <button
                            className="text-gray-500 hover:text-black"
                            onClick={() => navigate(`/Bookings`)}
                          >
                            <MdAdd />
                          </button>
                        </div>

                        <div className="flex flex-col gap-3 min-h-[100px]">
                          {tasks[status].map((task, index) => (
                            <Draggable
                              key={task.id}
                              draggableId={task.id.toString()}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`bg-[#DAE0CE] rounded-lg p-3 shadow-sm text-sm text-gray-700 transition ${
                                    snapshot.isDragging
                                      ? "ring-2 ring-green-600"
                                      : ""
                                  }`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium text-black">
                                        {task.title}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {task.desc}
                                      </p>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        className="text-gray-400 hover:text-black"
                                        onClick={() => navigate(`/Bookings`)}
                                      >
                                        <MdEdit size={16} />
                                      </button>
                                      <button
                                        className="text-gray-400 hover:text-red-600"
                                        onClick={() => navigate(`/Bookings`)}
                                      >
                                        <MdDelete size={16} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {tasks[status].length === 0 && (
                            <p className="text-xs text-gray-400 italic text-center py-6">
                              No tasks
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </Droppable>
                ))}
              </div>
            </DragDropContext>
          )}
        </div>
      </main>
    </div>
  );
};

export default KanbanBoard;
